// src/components/GodsView.jsx

import "chartjs-adapter-date-fns";
import "leaflet/dist/leaflet.css";
import "./godsView.css";

import {
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Decimation,
} from "chart.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  convertToFormat,
  getCompanyFormat,
  getCompanyId,
  getGroupFilterQuery,
  getHtmlMarker,
  getNumberFormat,
  showContent,
} from "../utilis/helper";
import {
  getEquipmentFilterDetails,
  getGroupSearchDetails,
  getParentGroupDetails,
} from "../utilis/searchHelper";

import { AsyncPaginate } from "react-select-async-paginate";
import L from "leaflet";
import { Line } from "react-chartjs-2";
import Select from "react-select";
import StreetViewModalCard from "./StreetViewModalCard";
import api from "../utilis/api";
import debounce from "lodash.debounce";
import loadingspinner from "../images/loading.gif";
import socketIOClient from "socket.io-client";
import { useIntl } from "react-intl";

const { parse, stringify } = require("wkt");

if (typeof window !== "undefined" && !window.L) {
  window.L = L;
}

require("leaflet.gridlayer.googlemutant");

const shortHistoryHighlightPlugin = {
  id: "shortHistoryHighlight",
  afterDatasetsDraw(chart, _args, opts) {
    const timestamp = opts?.timestamp;
    if (!Number.isFinite(timestamp)) return;
    const { ctx, chartArea, scales } = chart;
    if (!ctx || !chartArea || !scales?.x) return;
    const xScale = scales.x;
    const min =
      typeof xScale.min === "number" ? xScale.min : xScale.options?.min;
    const max =
      typeof xScale.max === "number" ? xScale.max : xScale.options?.max;
    if (Number.isFinite(min) && timestamp < min) return;
    if (Number.isFinite(max) && timestamp > max) return;
    const x = xScale.getPixelForValue(timestamp);
    if (!Number.isFinite(x)) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = opts?.lineWidth ?? 1.5;
    const dash = Array.isArray(opts?.dash) ? opts.dash : [4, 4];
    ctx.setLineDash(dash);
    ctx.strokeStyle = opts?.color || "rgba(37, 99, 235, 0.85)";
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  ChartTooltip,
  Legend,
  ChartTitle,
  Decimation,
  shortHistoryHighlightPlugin
);

//const webSocket = "wss://redis.intellitech.cl";
const webSocket = process.env.REACT_APP_WEBSOCKET;
//const webSocket = "ws://127.0.0.1:3001";

const SIXTY_MINUTES_MS = 60 * 60 * 1000;
const MAX_SERIES_POINTS = 9999;
const HISTORICAL_HOURS = 1;
const MAP_AUTOFOCUS_GRACE_MS = 5000;
const MOVING_SPEED_THRESHOLD_KMH = 0;
const STALE_POSITION_THRESHOLD_DAYS = 7;
const STALE_POSITION_THRESHOLD_MINUTES =
  STALE_POSITION_THRESHOLD_DAYS * 24 * 60;
const EQUIPMENTS_STATE_THROTTLE_MS = 1000;

const SPEED_VALUE_PATHS = [
  "speed",
  "speedKmh",
  "speed_kmh",
  "speedKmH",
  "speed_km_h",
  "attributes.speed",
  "attributes.speedKmh",
  "attributes.speed_kmh",
  "telemetry.speed",
  "telemetry.speedKmh",
  "telemetry.speed_kmh",
];

const RPM_VALUE_PATHS = ["attributes.rpm", "telemetry.rpm", "rpm"];

const ACCENT_STRIPPER = /[\u0300-\u036f]/g;

const normalizeSearchText = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value)
    .normalize("NFD")
    .replace(ACCENT_STRIPPER, "")
    .toLowerCase()
    .trim();
};

const resolveEquipmentLabel = (item, preferredKey) => {
  if (!item || typeof item !== "object") {
    return "";
  }
  const candidates = [
    preferredKey ? item?.[preferredKey] : undefined,
    item?.registerNumber,
    item?.name,
    item?.deviceId,
    item?.id,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) {
      continue;
    }
    const text = String(candidate).trim();
    if (text) {
      return text;
    }
  }
  return "";
};

const buildEquipmentSearchTokens = (labelText) => {
  const normalizedLabel = normalizeSearchText(labelText);
  if (!normalizedLabel) {
    return [];
  }
  const tokens = new Set([normalizedLabel]);
  normalizedLabel
    .split(/[\s_\-#/]+/)
    .filter(Boolean)
    .forEach((token) => tokens.add(token));
  return Array.from(tokens);
};

const STAT_ICONS = {
  total: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="6" rx="2" />
      <rect x="5" y="12" width="14" height="6" rx="2" />
      <circle cx="7" cy="11" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="17" cy="11" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="8" cy="19" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="16" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  ),
  moving: (
    <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#22c55e" />
      <path d="M12 5l5 5h-3v6H10V10H7l5-5z" fill="#ffffff" />
    </svg>
  ),
  halted: (
    <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
      <rect x="5" y="5" width="14" height="14" rx="3" fill="#f97316" />
      <rect
        x="7.5"
        y="7.5"
        width="9"
        height="9"
        rx="2"
        fill="none"
        stroke="#fff"
        strokeWidth="1.5"
      />
      <path d="M9 12h6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  stopped: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#dc2626"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="5" width="14" height="14" rx="3" fill="#dc2626" stroke="none" />
      <rect x="7.5" y="7.5" width="9" height="9" rx="2" fill="none" stroke="#fff" strokeWidth="1.5" />
      <path d="M9 12h6" stroke="#fff" />
    </svg>
  ),
  offline: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6b7280"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 10a12 12 0 0 1 16 0" />
      <path d="M7 13.5a7 7 0 0 1 10 0" />
      <path d="M10.5 17.5a2.5 2.5 0 0 1 3 0" />
      <path d="M3.5 3.5l17 17" />
    </svg>
  ),
};

const MetricLineChart = React.memo(function MetricLineChart({
  data,
  unit,
  loading,
  error,
  intl,
  highlightTimestamp,
  config = {},
}) {
  const sanitized = Array.isArray(data)
    ? data
      .map((point) => ({
        timestamp:
          typeof point?.timestamp === "number"
            ? point.timestamp
            : Number(point?.timestamp),
        value:
          typeof point?.value === "number"
            ? point.value
            : Number(point?.value),
      }))
      .filter(
        (point) =>
          Number.isFinite(point.timestamp) && Number.isFinite(point.value)
      )
    : [];

  const timeWindowMs = Number.isFinite(config?.timeWindowMs)
    ? config.timeWindowMs
    : SIXTY_MINUTES_MS;
  const now = Date.now();
  const minTimestamp = now - timeWindowMs;

  const filteredPoints = sanitized
    .filter(
      (point) => point.timestamp >= minTimestamp && point.timestamp <= now
    )
    .sort((a, b) => a.timestamp - b.timestamp);

  const highlightMs =
    typeof highlightTimestamp === "number" &&
      Number.isFinite(highlightTimestamp)
      ? highlightTimestamp
      : null;

  if (loading) {
    return (
      <div className="vehicle-bottom-bar__chart-status">
        {intl.formatMessage({
          id: "monitor.bottomBar.historyLoading",
          defaultMessage: "Loading 24h history...",
        })}
      </div>
    );
  }

  if (error) {
    return (
      <div className="vehicle-bottom-bar__chart-status vehicle-bottom-bar__chart-status--error">
        {error}
      </div>
    );
  }

  if (!filteredPoints.length) {
    return (
      <div className="vehicle-bottom-bar__chart-status">
        {intl.formatMessage({
          id: "monitor.bottomBar.noSeriesData",
          defaultMessage: "No chart data available",
        })}
      </div>
    );
  }

  const dataset = filteredPoints.map((point) => ({
    x: point.timestamp,
    y: point.value,
  }));
  const decimationEnabled = dataset.length > 500;
  const decimationSamples = decimationEnabled
    ? Math.min(500, dataset.length)
    : 0;

  const datasetMin = dataset.reduce(
    (acc, point) => Math.min(acc, point.y),
    Number.POSITIVE_INFINITY
  );
  const datasetMax = dataset.reduce(
    (acc, point) => Math.max(acc, point.y),
    Number.NEGATIVE_INFINITY
  );

  const hasConfiguredRange =
    Number.isFinite(config?.yMin) && Number.isFinite(config?.yMax);

  const fallbackMin = Number.isFinite(datasetMin) ? datasetMin : 0;
  const fallbackMax = Number.isFinite(datasetMax) ? datasetMax : 0;

  const yMin = hasConfiguredRange ? config.yMin : fallbackMin;
  const yMax = hasConfiguredRange ? config.yMax : fallbackMax;

  const paddingAmount = (yMax - yMin) * 0.1 || 1;
  const computedMin = Number.isFinite(config?.yMin)
    ? config.yMin
    : yMin - paddingAmount;
  const computedMax = Number.isFinite(config?.yMax)
    ? config.yMax
    : yMax + paddingAmount;

  const chartData = {
    datasets: [
      {
        label: config?.datasetLabel || unit || "value",
        data: dataset,
        borderColor: config?.color || "#2563eb",
        backgroundColor: config?.backgroundColor || "rgba(37, 99, 235, 0.15)",
        pointRadius: dataset.length > 1 ? 2 : 3,
        pointHoverRadius: 4,
        tension: 0.25,
        fill: config?.fill ?? false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    parsing: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    scales: {
      x: {
        type: "time",
        min: minTimestamp,
        max: now,
        time: {
          unit: config?.timeUnit || "minute",
          tooltipFormat: "PP p",
        },
        ticks: {
          maxTicksLimit: 6,
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: computedMin,
        max: computedMax,
        ticks: {
          maxTicksLimit: config?.yTicks || 6,
          callback: (value) => {
            const numeric = Number(value);
            const formatted = Number.isFinite(numeric)
              ? numeric.toString()
              : String(value);
            return unit ? `${getNumberFormat(formatted)}${unit ? ` ${unit}` : ""}` : getNumberFormat(formatted);
          },
        },
        grid: {
          color: "rgba(148, 163, 184, 0.2)",
          drawBorder: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      decimation: {
        enabled: decimationEnabled,
        algorithm: "lttb",
        samples: decimationSamples,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.parsed?.y;
            const numericVal = Number(val);
            const formatted = Number.isFinite(numericVal)
              ? getNumberFormat(numericVal)
              : String(val);
            return unit ? `${formatted} ${unit}` : formatted;
          },
        },
      },
    },
  };

  options.plugins.shortHistoryHighlight = {
    timestamp: highlightMs,
    color: "rgba(37, 99, 235, 0.85)",
    lineWidth: 1.5,
    dash: [4, 4],
  };

  return (
    <div className="vehicle-bottom-bar__chart-container">
      <Line
        data={chartData}
        options={options}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
});

const all = { value: -1, label: "Todos" };
// polyLineLayer moved to useRef inside component (see below)

const tileProxyBaseUrl = (process.env.REACT_APP_TILE_PROXY_URL || process.env.REACT_APP_BASE_URL || "")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");
const tileProxyUrl = tileProxyBaseUrl ? `${tileProxyBaseUrl}/tiles` : "/tiles";
const baseTileOptions = {
  maxZoom: 20,
  updateWhenIdle: true,
  updateWhenZooming: false,
  keepBuffer: 3,
  reuseTiles: true,
  detectRetina: false,
};

const buildMarkerIconKey = (entry, formatKey) => {
  if (!entry) return "";
  const type = entry.type || "";
  const displayName =
    type === "charging"
      ? entry.id ?? ""
      : entry?.[formatKey] || entry?.name || "";
  const course = entry.course ?? "";
  const speed = entry.speed ?? "";
  const fixtime = entry.fixtime ?? entry.fixTime ?? entry.fix_time ?? "";
  return `${type}|${displayName}|${course}|${speed}|${fixtime}`;
};

const tileLayers = (intl) => {
  return {
    [intl.formatMessage({ id: "routingmap.googleTerrain" })]: L.tileLayer(
      `${tileProxyUrl}/google-terrain/{z}/{x}/{y}.jpg`,
      { ...baseTileOptions, maxNativeZoom: 20 }
    ),
    [intl.formatMessage({ id: "routingmap.hybridMap" })]: L.tileLayer(
      `${tileProxyUrl}/google-hybrid/{z}/{x}/{y}.jpg`,
      { ...baseTileOptions, maxNativeZoom: 20 }
    ),
    [intl.formatMessage({ id: "routingmap.satelliteMap" })]: L.tileLayer(
      `${tileProxyUrl}/google-satellite/{z}/{x}/{y}.jpg`,
      { ...baseTileOptions, maxNativeZoom: 20 }
    ),
  };
};

let mapInstance = null;
// focusedMarkers moved to useRef inside component (see below)

export default function GodsView() {
  const [authUser, setAuthUser] = useState(null);
  const [equipments, setEquipments] = useState([]);
  const [equipmentsOptions, setEquipmentsOptions] = useState([]);

  const [parentGroup, setParentGroup] = useState(null);
  const [group, setGroup] = useState(null);
  const [equipment, setEquipment] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isBottomBarCollapsed, setIsBottomBarCollapsed] = useState(true);
  const [activeMetricsTab, setActiveMetricsTab] = useState("speed");
  const [shortHistoryActive, setShortHistoryActive] = useState(false);
  const [shortHistoryHighlight, setShortHistoryHighlight] = useState(null);
  const [isFollowingVehicle, setIsFollowingVehicle] = useState(false);
  const [hasParentGroupOptions, setHasParentGroupOptions] = useState(null);
  const [hasGroupOptions, setHasGroupOptions] = useState(null);

  const [IsStreetViewModal, setStreetViewModal] = useState(false);
  const [streetLocation, setStreetLocation] = useState([]);

  const [searchBy, setSearchBy] = useState(Math.random());
  const [searchBy1, setSearchBy1] = useState(Math.random());
  const [geoZones, setGeoZones] = useState(null);
  const [pageVisible, setPageVisible] = useState(!document.hidden);

  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const equipmentMarkersRef = useRef({});
  const equipmentsRef = useRef([]);
  const equipmentsByIdRef = useRef(new Map());
  const equipmentsOrderRef = useRef([]);
  const equipmentsStateTimerRef = useRef(null);
  const latestPositionsRef = useRef({});
  const latestHistoryRef = useRef([]);
  const programmaticMoveRef = useRef(false);
  const lastUserInteractionRef = useRef(0);
  const previousEquipmentValueRef = useRef(null);
  const isFollowingVehicleRef = useRef(false);
  const socketRef = useRef(null);
  const firstFitRef = useRef(true);
  const polyLineLayerRef = useRef([]);
  const focusedMarkersRef = useRef(new Set());
  const layerControlRef = useRef(null);
  const activePopupRef = useRef(null);
  const selectedVehicleRef = useRef(null);
  const shortHistoryPolylineRef = useRef(null);
  const shortHistoryMarkersRef = useRef([]);
  const shortHistoryPopupRef = useRef(null);
  const pendingMarkerUpdatesRef = useRef(new Map());
  const markerUpdateRafRef = useRef(null);
  const intl = useIntl();
  const equipmentFilterOption = useCallback((option, rawInput) => {
    if (!rawInput) {
      return true;
    }
    const query = normalizeSearchText(rawInput);
    if (!query) {
      return true;
    }
    if (option?.value === -1) {
      return false;
    }

    const labelMatch = normalizeSearchText(option?.label).includes(query);
    if (labelMatch) {
      return true;
    }

    const data = option?.data ?? option ?? {};
    const tokens =
      Array.isArray(data.searchTokens) && data.searchTokens.length
        ? data.searchTokens
        : buildEquipmentSearchTokens(option?.label);

    return tokens.some((token) => token.includes(query));
  }, []);

  useEffect(() => {
    isFollowingVehicleRef.current = isFollowingVehicle;
  }, [isFollowingVehicle]);

  const buildEquipmentsList = useCallback(() => {
    const order = equipmentsOrderRef.current;
    const byId = equipmentsByIdRef.current;
    if (!order.length) {
      return Array.from(byId.values());
    }
    return order.map((id) => byId.get(id)).filter(Boolean);
  }, []);

  const scheduleEquipmentsState = useCallback(
    (next, { immediate = false } = {}) => {
      equipmentsRef.current = Array.isArray(next) ? next : [];
      if (immediate) {
        if (equipmentsStateTimerRef.current) {
          clearTimeout(equipmentsStateTimerRef.current);
          equipmentsStateTimerRef.current = null;
        }
        setEquipments(equipmentsRef.current);
        return;
      }
      if (equipmentsStateTimerRef.current) {
        return;
      }
      equipmentsStateTimerRef.current = setTimeout(() => {
        equipmentsStateTimerRef.current = null;
        setEquipments(equipmentsRef.current);
      }, EQUIPMENTS_STATE_THROTTLE_MS);
    },
    [setEquipments]
  );

  const metricsTabs = useMemo(
    () => [
      {
        id: "speed",
        label: intl.formatMessage({
          id: "monitor.bottomBar.tabs.speed",
          defaultMessage: "Speed",
        }),
        unit: "km/h",
        keys: [
          "attributes.speed",
          "telemetry.speed",
          "speed",
          "speedKmh",
          "speed_kmh",
          "speedKmH",
          "speed_km_h",
        ],
        chart: {
          yMin: 0,
          yMax: 100,
          timeWindowMs: SIXTY_MINUTES_MS,
          yTicks: 6,
          color: "#2563eb",
        },
      },
      {
        id: "batteryVoltage",
        label: intl.formatMessage({
          id: "monitor.bottomBar.tabs.batteryVoltage",
          defaultMessage: "Battery Voltage",
        }),
        unit: "V",
        keys: [
          "attributes.power",
          "attributes.voltage",
          "telemetry.power",
          "telemetry.voltage",
          "batteryVoltage",
          "battery_voltage",
          "voltage",
          "batteryvoltage",
          "telemetry.batteryVoltage",
          "telemetry.battery_voltage",
        ],
        chart: {
          yMin: 0,
          yMax: 24,
          timeWindowMs: SIXTY_MINUTES_MS,
          yTicks: 5,
          color: "#f59e0b",
        },
      },
      {
        id: "batteryAmperage",
        label: intl.formatMessage({
          id: "monitor.bottomBar.tabs.batteryAmperage",
          defaultMessage: "Battery Amperage",
        }),
        unit: "A",
        keys: [
          "attributes.battery",
          "attributes.current",
          "telemetry.battery",
          "telemetry.current",
          "telemetry.battery_current",
          "batteryAmperage",
          "battery_amperage",
          "amperage",
          "current",
          "batteryCurrent",
          "telemetry.batteryAmperage",
        ],
        chart: {
          yMin: 0,
          yMax: 10,
          timeWindowMs: SIXTY_MINUTES_MS,
          yTicks: 5,
          color: "#10b981",
        },
      },
      {
        id: "rpm",
        label: intl.formatMessage({
          id: "monitor.bottomBar.tabs.rpm",
          defaultMessage: "RPM",
        }),
        unit: "rpm",
        keys: ["attributes.rpm", "telemetry.rpm", "rpm"],
        optional: true,
        chart: {
          timeWindowMs: SIXTY_MINUTES_MS,
          yTicks: 5,
          color: "#9333ea",
        },
      },
      {
        id: "soc",
        label: intl.formatMessage({
          id: "monitor.bottomBar.tabs.soc",
          defaultMessage: "State of Charge",
        }),
        unit: "%",
        keys: [
          "attributes.soc",
          "attributes.stateOfCharge",
          "telemetry.soc",
          "telemetry.stateOfCharge",
          "soc",
        ],
        optional: true,
        chart: {
          yMin: 0,
          yMax: 100,
          timeWindowMs: SIXTY_MINUTES_MS,
          yTicks: 5,
          color: "#ef4444",
        },
      },
    ],
    [intl]
  );

  const createEmptySeries = useCallback(() => {
    const template = {};
    metricsTabs.forEach((tab) => {
      template[tab.id] = [];
    });
    return template;
  }, [metricsTabs]);

  const metricsSeriesRef = useRef(createEmptySeries());
  const [vehicleMetricsSeries, setVehicleMetricsSeries] = useState(() =>
    createEmptySeries()
  );
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const [selectedVehicleHistoryKey, setSelectedVehicleHistoryKey] = useState(null);

  useEffect(() => {
    const resetSeries = createEmptySeries();
    metricsSeriesRef.current = resetSeries;
    setVehicleMetricsSeries(resetSeries);
  }, [createEmptySeries]);

  const parseTimestamp = useCallback((value) => {
    if (!value && value !== 0) return null;
    if (value instanceof Date) {
      const ts = value.getTime();
      return Number.isFinite(ts) ? ts : null;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
    const date = new Date(value);
    const timestamp = date.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }, []);

  const clearShortHistory = useCallback(() => {
    if (shortHistoryPolylineRef.current) {
      shortHistoryPolylineRef.current.remove();
      shortHistoryPolylineRef.current = null;
    }
    if (shortHistoryMarkersRef.current.length) {
      shortHistoryMarkersRef.current.forEach((marker) => marker.remove());
      shortHistoryMarkersRef.current = [];
    }
    if (shortHistoryPopupRef.current && mapRef.current) {
      mapRef.current.closePopup(shortHistoryPopupRef.current);
      shortHistoryPopupRef.current = null;
    }
    setShortHistoryActive(false);
    setShortHistoryHighlight(null);
  }, []);

  const focusMapView = useCallback(
    (target, zoom, { force = false } = {}) => {
      const map = mapRef.current;
      if (!map || target == null) {
        return;
      }
      const now = Date.now();
      const allowAuto = force || isFollowingVehicleRef.current;
      if (!allowAuto && now - lastUserInteractionRef.current < MAP_AUTOFOCUS_GRACE_MS) {
        return;
      }

      let latLng = null;
      if (typeof target?.getLatLng === "function") {
        const derived = target.getLatLng();
        if (
          derived &&
          Number.isFinite(derived.lat) &&
          Number.isFinite(derived.lng)
        ) {
          latLng = derived;
        }
      } else if (Array.isArray(target) && target.length >= 2) {
        const [lat, lng] = target;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          latLng = [lat, lng];
        }
      } else if (
        typeof target === "object" &&
        Number.isFinite(target?.lat) &&
        Number.isFinite(target?.lng)
      ) {
        latLng = target;
      }

      if (!latLng) {
        return;
      }

      const desiredZoom =
        typeof zoom === "number" && Number.isFinite(zoom)
          ? zoom
          : map.getZoom();

      const normalizedLatLng = Array.isArray(latLng)
        ? L.latLng(latLng[0], latLng[1])
        : L.latLng(latLng.lat, latLng.lng);

      programmaticMoveRef.current = true;
      lastUserInteractionRef.current = now;
      map.setView(normalizedLatLng, desiredZoom);
    },
    []
  );

  const toNumericValue = useCallback((value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const normalized = trimmed.replace(/,/g, ".");
      const numeric = Number(normalized);
      if (Number.isFinite(numeric)) {
        return numeric;
      }
      const match = normalized.match(/-?\d+(\.\d+)?/);
      if (match) {
        const extracted = Number(match[0]);
        return Number.isFinite(extracted) ? extracted : null;
      }
      return null;
    }
    return null;
  }, []);

  const getEntryCoordinates = useCallback(
    (entry) => {
      if (!entry) {
        return null;
      }
      const latCandidates = [
        entry.latitude,
        entry.lat,
        entry?.position?.lat,
        entry?.telemetry?.latitude,
        entry?.telemetry?.lat,
        entry?.attributes?.latitude,
        entry?.attributes?.lat,
      ];
      const lngCandidates = [
        entry.longitude,
        entry.lng,
        entry?.position?.lng,
        entry?.position?.lon,
        entry?.telemetry?.longitude,
        entry?.telemetry?.lng,
        entry?.telemetry?.lon,
        entry?.attributes?.longitude,
        entry?.attributes?.lng,
        entry?.attributes?.lon,
      ];

      let lat = null;
      let lng = null;

      for (const candidate of latCandidates) {
        const numeric = toNumericValue(candidate);
        if (numeric !== null) {
          lat = numeric;
          break;
        }
      }

      for (const candidate of lngCandidates) {
        const numeric = toNumericValue(candidate);
        if (numeric !== null) {
          lng = numeric;
          break;
        }
      }

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      return [lat, lng];
    },
    [toNumericValue]
  );

  const handleCenterOnVehicle = useCallback(() => {
    const selection = selectedVehicleRef.current;
    if (!selection) {
      return;
    }
    const registerNumber = selection.registerNumber;
    const marker =
      registerNumber != null
        ? equipmentMarkersRef.current[registerNumber]
        : null;
    if (marker) {
      focusMapView(marker, 17, { force: true });
      marker.openPopup();
      return;
    }
    const coordinates = getEntryCoordinates(selection);
    if (coordinates) {
      focusMapView(coordinates, 17, { force: true });
    }
  }, [focusMapView, getEntryCoordinates]);

  const toggleFollowVehicle = useCallback(() => {
    const currentSelection = selectedVehicleRef.current;
    setIsFollowingVehicle((prev) => {
      const next = !prev;
      isFollowingVehicleRef.current = next;
      if (next && currentSelection) {
        handleCenterOnVehicle();
      }
      return next;
    });
  }, [handleCenterOnVehicle]);

  const isIgnitionOn = useCallback((entry) => {
    if (!entry) return false;
    const candidate =
      entry?.ignition ??
      entry?.attributes?.ignition ??
      entry?.attributes?.Ignition ??
      entry?.telemetry?.ignition ??
      entry?.telemetry?.Ignition ??
      entry?.attributes?.ignitionStatus ??
      entry?.attributes?.IgnitionStatus ??
      entry?.telemetry?.ignitionStatus;

    if (typeof candidate === "string") {
      const normalized = candidate.trim().toLowerCase();
      if (!normalized) return false;
      return normalized === "true" || normalized === "1" || normalized === "on";
    }
    if (typeof candidate === "number") {
      return candidate !== 0;
    }
    return Boolean(candidate);
  }, []);

  const appendMetricPoint = useCallback(
    (tabId, timestampMs, rawValue) => {
      if (!Number.isFinite(timestampMs)) return;
      const numericValue = toNumericValue(rawValue);
      if (numericValue === null) return;

      const cutoff = Date.now() - SIXTY_MINUTES_MS;
      const existingSeries = metricsSeriesRef.current[tabId] || [];
      const lastPoint = existingSeries[existingSeries.length - 1];
      let nextSeries;

      if (
        lastPoint &&
        Math.abs(lastPoint.timestamp - timestampMs) < 1000
      ) {
        nextSeries = [
          ...existingSeries.slice(0, -1),
          { timestamp: timestampMs, value: numericValue },
        ];
      } else {
        nextSeries = [
          ...existingSeries,
          { timestamp: timestampMs, value: numericValue },
        ];
      }

      nextSeries = nextSeries.filter((point) => point.timestamp >= cutoff);
      if (nextSeries.length > MAX_SERIES_POINTS) {
        nextSeries = nextSeries.slice(nextSeries.length - MAX_SERIES_POINTS);
      }

      metricsSeriesRef.current = {
        ...metricsSeriesRef.current,
        [tabId]: nextSeries,
      };
      setVehicleMetricsSeries((prev) => ({
        ...prev,
        [tabId]: nextSeries,
      }));
    },
    [toNumericValue]
  );

  const resolveMetricValue = useCallback((vehicle, keys = []) => {
    if (!vehicle || !Array.isArray(keys) || !keys.length) {
      return null;
    }
    for (const keyPath of keys) {
      const segments = keyPath.split(".");
      let current = vehicle;
      for (const segment of segments) {
        if (current == null || typeof current !== "object") {
          current = undefined;
          break;
        }
        current = current[segment];
      }
      if (current === undefined || current === null) {
        continue;
      }
      if (typeof current === "string" && current.trim() === "") {
        continue;
      }
      return current;
    }
    return null;
  }, []);

  const resolveEntryTimestamp = useCallback(
    (entry) =>
      parseTimestamp(
        entry?.fixtime ??
        entry?.fixTime ??
        entry?.fix_time ??
        entry?.deviceTime ??
        entry?.device_time ??
        entry?.timestamp ??
        entry?.createdAt ??
        entry?.updatedAt ??
        entry?.serverTime ??
        entry?.server_time
      ),
    [parseTimestamp]
  );

  const equipmentStatusSummary = useMemo(() => {
    const createInitial = () => ({
      total: 0,
      moving: 0,
      halted: 0,
      stopped: 0,
      withoutSignal: 0,
    });

    if (!Array.isArray(equipments) || !equipments.length) {
      return createInitial();
    }

    const now = Date.now();

    return equipments.reduce((acc, entry) => {
      if (!entry) {
        return acc;
      }

      acc.total += 1;

      const timestampMs = resolveEntryTimestamp(entry);
      const minutesSinceUpdate = Number.isFinite(timestampMs)
        ? Math.max(0, (now - timestampMs) / 60000)
        : Infinity;

      if (
        !Number.isFinite(minutesSinceUpdate) ||
        minutesSinceUpdate > STALE_POSITION_THRESHOLD_MINUTES
      ) {
        acc.withoutSignal += 1;
        return acc;
      }

      const speedValueRaw = resolveMetricValue(entry, SPEED_VALUE_PATHS);
      const speedKmh = toNumericValue(speedValueRaw) ?? 0;
      const ignitionOn = isIgnitionOn(entry);
      const rpmValueRaw = resolveMetricValue(entry, RPM_VALUE_PATHS);
      const rpmValue = toNumericValue(rpmValueRaw);
      const rpmActive = rpmValue !== null && rpmValue > 0;

      if (ignitionOn && speedKmh > MOVING_SPEED_THRESHOLD_KMH) {
        acc.moving += 1;
        return acc;
      }

      if (ignitionOn && (speedKmh <= MOVING_SPEED_THRESHOLD_KMH || rpmActive)) {
        acc.halted += 1;
        return acc;
      }

      if (!ignitionOn && speedKmh <= MOVING_SPEED_THRESHOLD_KMH) {
        acc.stopped += 1;
        return acc;
      }

      acc.stopped += 1;
      return acc;
    }, createInitial());
  }, [
    equipments,
    isIgnitionOn,
    resolveEntryTimestamp,
    resolveMetricValue,
    toNumericValue,
  ]);

  const monitorStatsCards = useMemo(
    () => [
      {
        key: "total",
        label: intl.formatMessage({
          id: "monitor.stats.total",
          defaultMessage: "Total vehicles",
        }),
        value: equipmentStatusSummary.total,
        accent: "all",
        icon: STAT_ICONS.total,
      },
      {
        key: "moving",
        label: intl.formatMessage({
          id: "monitor.stats.moving",
          defaultMessage: "Moving",
        }),
        value: equipmentStatusSummary.moving,
        accent: "moving",
        icon: STAT_ICONS.moving,
      },
      {
        key: "halted",
        label: intl.formatMessage({
          id: "monitor.stats.halted",
          defaultMessage: "Idling",
        }),
        value: equipmentStatusSummary.halted,
        accent: "halted",
        icon: STAT_ICONS.halted,
      },
      {
        key: "stopped",
        label: intl.formatMessage({
          id: "monitor.stats.stopped",
          defaultMessage: "Stopped",
        }),
        value: equipmentStatusSummary.stopped,
        accent: "stopped",
        icon: STAT_ICONS.stopped,
      },
      {
        key: "withoutSignal",
        label: intl.formatMessage(
          {
            id: "monitor.stats.withoutSignal",
            defaultMessage: "Without signal (>{days} days)",
          },
          { days: STALE_POSITION_THRESHOLD_DAYS }
        ),
        value: equipmentStatusSummary.withoutSignal,
        accent: "offline",
        icon: STAT_ICONS.offline,
      },
    ],
    [equipmentStatusSummary, intl]
  );

  const buildShortHistoryPath = useCallback(
    (history, fallbackPoint) => {
      const coords = [];
      const nodes = [];

      if (!Array.isArray(history) || !history.length) {
        if (fallbackPoint) {
          coords.push(fallbackPoint);
          nodes.push({ entry: null, point: fallbackPoint });
        }
        return { coords, nodes };
      }

      let lastIgnitionStartIndex = -1;
      let previousIgnition = false;
      history.forEach((entry, index) => {
        const ignitionOn = isIgnitionOn(entry);
        if (ignitionOn && !previousIgnition) {
          lastIgnitionStartIndex = index;
        }
        previousIgnition = ignitionOn;
      });

      const startIndex =
        lastIgnitionStartIndex !== -1
          ? lastIgnitionStartIndex
          : Math.max(history.length - 20, 0);

      for (let i = startIndex; i < history.length; i += 1) {
        const entry = history[i];
        const point = getEntryCoordinates(entry);
        if (point) {
          coords.push(point);
          nodes.push({ entry, point });
        }
      }

      if (fallbackPoint) {
        if (
          !coords.length ||
          Math.abs(coords[coords.length - 1][0] - fallbackPoint[0]) > 1e-6 ||
          Math.abs(coords[coords.length - 1][1] - fallbackPoint[1]) > 1e-6
        ) {
          coords.push(fallbackPoint);
          nodes.push({ entry: null, point: fallbackPoint });
        }
      }

      return { coords, nodes };
    },
    [getEntryCoordinates, isIgnitionOn]
  );

  const resolveOrientationLabel = useCallback(
    (courseValue) => {
      const numeric = toNumericValue(courseValue);
      if (numeric === null) {
        return intl.formatMessage({
          id: "monitor.bottomBar.shortHistory.noOrientation",
          defaultMessage: "N/A",
        });
      }
      const normalized = ((numeric % 360) + 360) % 360;
      const directions = [
        { id: "north", defaultMessage: "North" },
        { id: "northeast", defaultMessage: "North-East" },
        { id: "east", defaultMessage: "East" },
        { id: "southeast", defaultMessage: "South-East" },
        { id: "south", defaultMessage: "South" },
        { id: "southwest", defaultMessage: "South-West" },
        { id: "west", defaultMessage: "West" },
        { id: "northwest", defaultMessage: "North-West" },
      ];
      const index = Math.round(normalized / 45) % directions.length;
      const direction = directions[index];
      return intl.formatMessage({
        id: `monitor.bottomBar.shortHistory.orientation.${direction.id}`,
        defaultMessage: direction.defaultMessage,
      });
    },
    [intl, toNumericValue]
  );

  const formatDateTimeLabel = useCallback(
    (value) => {
      const timestampMs = parseTimestamp(value);
      if (!Number.isFinite(timestampMs)) {
        return intl.formatMessage({
          id: "monitor.bottomBar.shortHistory.noTimestamp",
          defaultMessage: "Timestamp unavailable",
        });
      }
      const date = new Date(timestampMs);
      const pad = (input) => String(input).padStart(2, "0");
      const day = pad(date.getDate());
      const month = pad(date.getMonth() + 1);
      const year = date.getFullYear();
      let hours = date.getHours();
      const meridiem = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      if (hours === 0) hours = 12;
      const timePart = `${pad(hours)}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )} ${meridiem}`;
      return `${day}-${month}-${year}  ${timePart}`;
    },
    [intl, parseTimestamp]
  );

  const appendRealtimeMetrics = useCallback(
    (vehicle) => {
      if (!vehicle) return;
      const resolved = resolveEntryTimestamp(vehicle);
      const timestampMs = Number.isFinite(resolved) ? resolved : Date.now();
      if (!Number.isFinite(timestampMs)) return;
      metricsTabs.forEach((tab) => {
        const rawValue = resolveMetricValue(vehicle, tab.keys);
        if (rawValue === undefined || rawValue === null) return;
        appendMetricPoint(tab.id, timestampMs, rawValue);
      });
      const history = latestHistoryRef.current;
      if (Array.isArray(history)) {
        const lastEntry = history[history.length - 1];
        const lastTimestamp = lastEntry
          ? resolveEntryTimestamp(lastEntry)
          : null;
        if (!Number.isFinite(lastTimestamp) || timestampMs > lastTimestamp) {
          history.push({ ...vehicle });
          if (history.length > MAX_SERIES_POINTS) {
            history.splice(0, history.length - MAX_SERIES_POINTS);
          }
        }
      }
    },
    [appendMetricPoint, metricsTabs, resolveEntryTimestamp, resolveMetricValue]
  );

  const fetchHistoricalPositions = useCallback(
    async (vehicleSnapshot) => {
      if (!vehicleSnapshot) return;
      setMetricsLoading(true);
      setMetricsError(null);

      const equipmentIdentifier =
        vehicleSnapshot.id ??
        vehicleSnapshot.value ??
        vehicleSnapshot.equipmentId ??
        null;

      const historyHours = HISTORICAL_HOURS;

      const payload = {
        channel: "intellitech:positions",
        hours: historyHours,
        registerNumber: vehicleSnapshot.registerNumber,
        equipmentId: equipmentIdentifier,
        imei: vehicleSnapshot.imei,
        deviceId: equipmentIdentifier,
      };

      const normaliseHistoryCollection = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw !== "object") return [];
        const candidates = [
          raw.data,
          raw.data?.rows,
          raw.data?.data,
          raw.data?.items,
          raw.data?.history,
          raw.data?.history?.rows,
          raw.rows,
          raw.history,
          raw.history?.rows,
          raw.positions,
          raw.items,
          raw.results,
          raw.list,
          raw.payload,
          raw.payload?.rows,
        ];
        for (const candidate of candidates) {
          if (Array.isArray(candidate)) {
            return candidate;
          }
        }
        return [];
      };

      const isLikelyHistoryEntry = (entry) => {
        if (!entry || typeof entry !== "object") {
          return false;
        }
        const timestampKeys = [
          "timestamp",
          "timeStamp",
          "Timestamp",
          "timestampMs",
          "timestamp_ms",
          "fixtime",
          "fixTime",
          "fix_time",
          "createdAt",
          "created_at",
          "updatedAt",
          "updated_at",
          "deviceTime",
          "time",
          "eventTime",
          "event_time",
          "eventTimestamp",
          "event_timestamp",
          "recordedAt",
          "recorded_at",
          "recordedTime",
          "recorded_time",
          "collectedAt",
          "collected_at",
          "loggedAt",
          "logged_at",
          "datetime",
          "dateTime",
          "dt",
          "ts",
          "epoch",
          "epochMs",
          "epoch_ms",
        ];
        if (
          timestampKeys.some(
            (key) => entry[key] !== undefined && entry[key] !== null
          )
        ) {
          return true;
        }
        const inspectNested = (candidate) => {
          if (!candidate || typeof candidate !== "object") {
            return false;
          }
          return timestampKeys.some(
            (key) =>
              candidate[key] !== undefined && candidate[key] !== null
          );
        };
        if (inspectNested(entry.telemetry) || inspectNested(entry.attributes)) {
          return true;
        }
        if (typeof entry.attributes === "string") {
          try {
            const parsed = JSON.parse(entry.attributes);
            if (inspectNested(parsed)) {
              return true;
            }
          } catch (_err) {
            // ignore malformed attribute payloads
          }
        }
        return false;
      };

      const normaliseHistorySeries = (raw) => {
        const collection = normaliseHistoryCollection(raw);
        if (!collection.length) {
          return [];
        }
        const filtered = collection.filter((entry) =>
          isLikelyHistoryEntry(entry)
        );
        return filtered.length ? filtered : [];
      };

      const findEquipmentNode = (root) => {
        const expectedId =
          payload?.equipmentId ?? payload?.deviceId ?? null;
        if (!root || expectedId == null) {
          return null;
        }
        const visited = new Set();
        const stack = [root];
        while (stack.length) {
          const current = stack.pop();
          if (!current || typeof current !== "object") {
            continue;
          }
          if (visited.has(current)) {
            continue;
          }
          visited.add(current);

          if (Array.isArray(current)) {
            for (const item of current) {
              if (item && typeof item === "object") {
                stack.push(item);
              }
            }
            continue;
          }

          const identifiers = [
            current?.equipmentId,
            current?.equipment_id,
            current?.deviceId,
            current?.device_id,
            current?.id,
            current?.imei,
          ]
            .filter((value) => value !== undefined && value !== null)
            .map(String);

          if (identifiers.includes(String(expectedId))) {
            return current;
          }

          for (const key of Object.keys(current)) {
            const value = current[key];
            if (value && typeof value === "object") {
              stack.push(value);
            }
          }
        }
        return null;
      };

      const findHistoryArray = (root) => {
        if (!root) return [];
        const visited = new Set();
        const stack = [root];
        while (stack.length) {
          const current = stack.pop();
          if (!current) {
            continue;
          }
          if (visited.has(current)) {
            continue;
          }
          visited.add(current);

          if (Array.isArray(current)) {
            const objects = current.filter(
              (item) => item && typeof item === "object"
            );
            if (!objects.length) {
              continue;
            }
            const filtered = objects.filter((entry) =>
              isLikelyHistoryEntry(entry)
            );
            if (filtered.length) {
              return filtered;
            }
            for (const item of objects) {
              stack.push(item);
            }
            continue;
          }

          if (typeof current !== "object") {
            continue;
          }

          for (const key of Object.keys(current)) {
            const value = current[key];
            if (value && typeof value === "object") {
              stack.push(value);
            }
          }
        }
        return [];
      };

      const extractLiveEquipmentHistory = (raw) => {
        const directSeries = normaliseHistorySeries(raw);
        if (directSeries.length) {
          return directSeries;
        }
        const equipmentNode = findEquipmentNode(raw);
        const seriesFromNode = equipmentNode
          ? findHistoryArray(equipmentNode)
          : [];
        if (seriesFromNode.length) {
          return seriesFromNode;
        }
        return findHistoryArray(raw);
      };

      const attemptRequests = async () => {
        const equipmentId = payload.equipmentId ?? payload.deviceId ?? null;
        if (!equipmentId) return [];

        const searchParams = new URLSearchParams({
          filter_equipmentId: String(equipmentId),
        });

        if (payload.registerNumber) {
          searchParams.append("registerNumber", payload.registerNumber);
        }
        if (payload.imei) {
          searchParams.append("imei", String(payload.imei));
        }
        if (payload.hours) {
          searchParams.append("hours", String(payload.hours));
        }
        searchParams.append("limit", MAX_SERIES_POINTS);

        if (authUser?.company?.id) {
          searchParams.append(
            "filter_companyId",
            String(authUser.company.id)
          );
        }
        if (authUser?.token) {
          searchParams.append("token", authUser.token);
        }

        const response = await api.getApi(
          `liveequipment?${searchParams.toString()}`
        );
        return extractLiveEquipmentHistory(response);
      };

      try {
        const collection = await attemptRequests();
        const cutoff = Date.now() - SIXTY_MINUTES_MS;
        const nextSeries = createEmptySeries();
        const safeParseAttributes = (entry) => {
          if (
            entry &&
            typeof entry.attributes === "string" &&
            entry.attributes.trim().startsWith("{")
          ) {
            try {
              return {
                ...entry,
                attributes: JSON.parse(entry.attributes),
              };
            } catch (_err) {
              return entry;
            }
          }
          return entry;
        };

        const sortedCollection = Array.isArray(collection)
          ? [...collection].sort((a, b) => {
            const aTs = resolveEntryTimestamp(a);
            const bTs = resolveEntryTimestamp(b);
            const aValid = Number.isFinite(aTs);
            const bValid = Number.isFinite(bTs);
            if (!aValid && !bValid) return 0;
            if (!aValid) return 1;
            if (!bValid) return -1;
            return aTs - bTs;
          })
          : [];

        latestHistoryRef.current = sortedCollection;

        sortedCollection.forEach((rawEntry) => {
          const entry = safeParseAttributes(rawEntry);
          const timestampMs = resolveEntryTimestamp(entry);
          if (!Number.isFinite(timestampMs) || timestampMs < cutoff) {
            return;
          }
          metricsTabs.forEach((tab) => {
            const rawValue = resolveMetricValue(entry, tab.keys);
            if (rawValue === undefined || rawValue === null) {
              return;
            }
            const numericValue = toNumericValue(rawValue);
            if (numericValue === null) return;
            const series = nextSeries[tab.id];
            const lastPoint = series[series.length - 1];
            if (lastPoint && Math.abs(lastPoint.timestamp - timestampMs) < 1000) {
              series[series.length - 1] = {
                timestamp: timestampMs,
                value: numericValue,
              };
            } else {
              series.push({ timestamp: timestampMs, value: numericValue });
            }
          });
        });

        metricsTabs.forEach((tab) => {
          const series = nextSeries[tab.id];
          if (series.length > MAX_SERIES_POINTS) {
            nextSeries[tab.id] = series.slice(
              series.length - MAX_SERIES_POINTS
            );
          }
        });

        metricsSeriesRef.current = nextSeries;
        setVehicleMetricsSeries(nextSeries);
        appendRealtimeMetrics(vehicleSnapshot);
      } catch (error) {
        console.error("Failed to load historical positions", error);
        setMetricsError(
          intl.formatMessage({
            id: "monitor.bottomBar.historyError",
            defaultMessage: "Unable to load historical positions.",
          })
        );
        latestHistoryRef.current = [];
        clearShortHistory();
        const resetSeries = createEmptySeries();
        metricsSeriesRef.current = resetSeries;
        setVehicleMetricsSeries(resetSeries);
        appendRealtimeMetrics(vehicleSnapshot);
      } finally {
        setMetricsLoading(false);
      }
    },
    [
      appendRealtimeMetrics,
      clearShortHistory,
      createEmptySeries,
      authUser,
      intl,
      metricsTabs,
      resolveEntryTimestamp,
      resolveMetricValue,
      toNumericValue,
    ]
  );

  const handleVehicleSelection = useCallback(
    (
      vehicle,
      { resetTab = true, forceExpand = true, skipHistory = false } = {}
    ) => {
      if (!vehicle) {
        clearShortHistory();
        latestHistoryRef.current = [];
        selectedVehicleRef.current = null;
        isFollowingVehicleRef.current = false;
        setIsFollowingVehicle(false);
        setSelectedVehicle(null);
        if (resetTab) {
          setActiveMetricsTab("speed");
        }
        setIsBottomBarCollapsed(true);
        setMetricsError(null);
        setMetricsLoading(false);
        setSelectedVehicleHistoryKey(null);
        const resetSeries = createEmptySeries();
        metricsSeriesRef.current = resetSeries;
        setVehicleMetricsSeries(resetSeries);
        return;
      }

      const snapshot = { ...vehicle };
      selectedVehicleRef.current = snapshot;
      setSelectedVehicle(snapshot);
      if (resetTab) {
        setActiveMetricsTab("speed");
      }
      if (forceExpand) {
        setIsBottomBarCollapsed(false);
      }
      setMetricsError(null);
      if (!skipHistory) {
        clearShortHistory();
        latestHistoryRef.current = [];
        const resetSeries = createEmptySeries();
        metricsSeriesRef.current = resetSeries;
        setVehicleMetricsSeries(resetSeries);
        setMetricsLoading(true);
      }
      appendRealtimeMetrics(snapshot);

      if (!skipHistory) {
        const identity =
          snapshot?.id != null
            ? `id:${snapshot.id}`
            : snapshot?.registerNumber
              ? `reg:${snapshot.registerNumber}`
              : snapshot?.imei
                ? `imei:${snapshot.imei}`
                : snapshot?.name
                  ? `name:${snapshot.name}`
                  : "rand";
        const selectionToken = `${identity}|${Date.now()}`;
        setSelectedVehicleHistoryKey(selectionToken);
      }
    },
    [
      appendRealtimeMetrics,
      clearShortHistory,
      createEmptySeries,
      setActiveMetricsTab,
      setIsBottomBarCollapsed,
      setMetricsError,
      setMetricsLoading,
      setSelectedVehicle,
      setVehicleMetricsSeries,
    ]
  );

  const handleRedisStream = useCallback(
    (payload) => {
      const updates = Array.isArray(payload) ? payload : [payload];
      updates.forEach((entry) => {
        if (!entry) return;
        const currentSelection = selectedVehicleRef.current;
        if (!currentSelection) return;
        const matches =
          (currentSelection.id != null && entry.id === currentSelection.id) ||
          (currentSelection.registerNumber &&
            entry.registerNumber === currentSelection.registerNumber) ||
          (currentSelection.imei && entry.imei === currentSelection.imei);
        if (!matches) return;
        const mergedTelemetry =
          currentSelection.telemetry || entry.telemetry
            ? {
              ...(currentSelection.telemetry || {}),
              ...(entry.telemetry || {}),
            }
            : undefined;
        const mergedSnapshot = {
          ...currentSelection,
          ...entry,
          ...(mergedTelemetry ? { telemetry: mergedTelemetry } : {}),
        };
        handleVehicleSelection(mergedSnapshot, {
          resetTab: false,
          forceExpand: false,
          skipHistory: true,
        });
      });
    },
    [handleVehicleSelection]
  );

  const handleShortHistoryClick = useCallback(async () => {
    const currentVehicle = selectedVehicleRef.current;
    if (!currentVehicle) {
      return;
    }

    if (shortHistoryPolylineRef.current) {
      clearShortHistory();
      return;
    }

    if (!latestHistoryRef.current.length) {
      await fetchHistoricalPositions(currentVehicle);
    }

    const history = latestHistoryRef.current;
    const fallbackPoint = getEntryCoordinates(currentVehicle);
    const { coords, nodes } = buildShortHistoryPath(history, fallbackPoint);
    if (!coords.length) {
      console.warn(
        "Short history: no coordinates available for the selected vehicle."
      );
      return;
    }

    const map = mapRef.current;
    if (!map) {
      return;
    }

    clearShortHistory();

    shortHistoryPolylineRef.current = L.polyline(coords, {
      color: "#2563eb",
      weight: 4,
      opacity: 0.85,
      dashArray: "8 6",
    }).addTo(map);

    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const toDegrees = (radians) => (radians * 180) / Math.PI;
    const deriveBearing = (from, to) => {
      const lat1 = toRadians(from[0]);
      const lat2 = toRadians(to[0]);
      const deltaLng = toRadians(to[1] - from[1]);
      const y = Math.sin(deltaLng) * Math.cos(lat2);
      const x =
        Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
      const brng = (toDegrees(Math.atan2(y, x)) + 360) % 360;
      return brng;
    };

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const markers = [];
    for (let i = 1; i < nodes.length; i += 1) {
      const node = nodes[i];
      const prevNode = nodes[i - 1];
      if (!node?.point || !prevNode?.point) {
        continue;
      }

      const bearing = deriveBearing(prevNode.point, node.point);
      const iconHtml = `
        <div class="short-history-arrow__wrapper">
          <svg class="short-history-arrow__icon" viewBox="0 0 24 24" style="transform: rotate(${bearing}deg);">
            <path d="M12 2l6 9h-4v11h-4V11H6z" />
          </svg>
        </div>
      `;

      const icon = L.divIcon({
        html: iconHtml,
        className: "short-history-arrow",
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });

      const marker = L.marker(node.point, {
        icon,
        interactive: true,
        keyboard: false,
        opacity: 0.95,
      }).addTo(map);

      marker.on("click", () => {
        const entry = node.entry || currentVehicle;
        const timestampMs = resolveEntryTimestamp(entry);
        const address =
          entry?.address ??
          entry?.attributes?.address ??
          entry?.attributes?.Address ??
          currentVehicle?.address ??
          intl.formatMessage({
            id: "monitor.bottomBar.shortHistory.noAddress",
            defaultMessage: "Address unavailable",
          });
        const ignition = isIgnitionOn(entry);
        const ignitionValue = intl.formatMessage({
          id: ignition
            ? "monitor.bottomBar.shortHistory.ignitionStateOn"
            : "monitor.bottomBar.shortHistory.ignitionStateOff",
          defaultMessage: ignition ? "On" : "Off",
        });

        const speedValueRaw =
          entry?.speed ??
          entry?.telemetry?.speed ??
          entry?.attributes?.speed ??
          currentVehicle?.speed ??
          null;
        const speedNumeric = toNumericValue(speedValueRaw);
        const speedValue =
          speedNumeric !== null
            ? `${getNumberFormat(speedNumeric)} km/h`
            : intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.noSpeed",
              defaultMessage: "N/A",
            });

        const courseRaw =
          entry?.course ??
          entry?.attributes?.course ??
          entry?.attributes?.Course ??
          entry?.telemetry?.course ??
          entry?.telemetry?.Course ??
          currentVehicle?.course ??
          null;
        const orientationValue = resolveOrientationLabel(courseRaw);

        const timestampLabel = formatDateTimeLabel(timestampMs);
        const highlightTimestamp =
          Number.isFinite(timestampMs) && timestampMs !== null
            ? timestampMs
            : null;
        setShortHistoryHighlight(highlightTimestamp);

        if (!shortHistoryPopupRef.current) {
          shortHistoryPopupRef.current = L.popup({
            closeButton: false,
            autoPan: true,
            className: "short-history-popup",
          });
        }

        const detailRows = [
          {
            label: intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.timestamp",
            }),
            value: timestampLabel,
          },
          {
            label: intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.address",
            }),
            value: address,
          },
          {
            label: intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.speed",
            }),
            value: speedValue,
          },
          {
            label: intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.orientation",
            }),
            value: orientationValue,
          },
          {
            label: intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.ignition",
            }),
            value: ignitionValue,
          },
        ].filter(
          (row) => row.value !== null && row.value !== undefined && row.value !== ""
        );

        const rowsHtml = detailRows
          .map(
            ({ label, value }) => `
            <div class="short-history-popup__line">
              <span class="short-history-popup__label">${escapeHtml(label)}</span>
              <span class="short-history-popup__separator">:</span>
              <span class="short-history-popup__value">${escapeHtml(
              value
            )}</span>
            </div>`
          )
          .join("");

        const closeLabel = intl.formatMessage({
          id: "monitor.bottomBar.shortHistory.close",
          defaultMessage: "Close",
        });

        const popupContent = `
          <div class="short-history-popup__content">
            <button type="button" class="short-history-popup__close" aria-label="${escapeHtml(
          closeLabel
        )}">&times;</button>
            ${rowsHtml}
          </div>
        `;

        const popupInstance = shortHistoryPopupRef.current;
        popupInstance.setLatLng(node.point).setContent(popupContent);

        if (map) {
          popupInstance.once("add", () => {
            const element = popupInstance.getElement();
            if (element) {
              const closeButton = element.querySelector(
                ".short-history-popup__close"
              );
              if (closeButton) {
                closeButton.addEventListener("click", (event) => {
                  event.preventDefault();
                  map.closePopup(popupInstance);
                });
              }
            }
            popupInstance.once("remove", () => {
              setShortHistoryHighlight(null);
            });
          });

          map.openPopup(popupInstance);
        }
      });

      markers.push(marker);
    }

    shortHistoryMarkersRef.current = markers;
    setShortHistoryActive(true);

    try {
      const bounds = shortHistoryPolylineRef.current.getBounds();
      if (bounds?.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
      }
    } catch (err) {
      console.warn("Short history: unable to adjust map bounds.", err);
    }
  }, [
    authUser,
    buildShortHistoryPath,
    clearShortHistory,
    fetchHistoricalPositions,
    formatDateTimeLabel,
    getEntryCoordinates,
    intl,
    isIgnitionOn,
    resolveEntryTimestamp,
    resolveOrientationLabel,
    toNumericValue,
    setShortHistoryHighlight,
  ]);

  const formatMetricValue = useCallback(
    (value, unit) => {
      if (
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "")
      ) {
        return intl.formatMessage({
          id: "monitor.bottomBar.noData",
          defaultMessage: "No data available",
        });
      }

      if (typeof value === "number") {
        if (!Number.isFinite(value)) {
          return intl.formatMessage({
            id: "monitor.bottomBar.noData",
            defaultMessage: "No data available",
          });
        }
        const display = getNumberFormat(value);
        return unit ? `${display} ${unit}` : display;
      }

      if (typeof value === "string") {
        const trimmed = value.trim();
        const numeric = Number(trimmed);
        if (trimmed !== "" && !Number.isNaN(numeric) && Number.isFinite(numeric)) {
          const display = getNumberFormat(numeric);
          return unit ? `${display} ${unit}` : display;
        }
        return trimmed;
      }

      return String(value);
    },
    [intl]
  );

  const selectedVehicleLabel = useMemo(() => {
    if (!selectedVehicle) return "";
    const formatKey = authUser ? getCompanyFormat(authUser) : "registerNumber";
    return (
      selectedVehicle[formatKey] ||
      selectedVehicle.registerNumber ||
      selectedVehicle.name ||
      ""
    );
  }, [selectedVehicle, authUser]);

  const selectedDriverName = useMemo(() => {
    if (!selectedVehicle) return "";
    return selectedVehicle.driverName || selectedVehicle.driver || "";
  }, [selectedVehicle]);

  const availableMetricsTabs = useMemo(() => {
    return metricsTabs.filter((tab) => {
      if (!tab.optional) {
        return true;
      }
      const liveValue = selectedVehicle
        ? resolveMetricValue(selectedVehicle, tab.keys)
        : null;
      const hasLiveValue =
        liveValue !== null &&
        liveValue !== undefined &&
        !(typeof liveValue === "string" && liveValue.trim() === "");
      const series = vehicleMetricsSeries[tab.id];
      const hasSeries = Array.isArray(series) && series.length > 0;
      return hasLiveValue || hasSeries;
    });
  }, [metricsTabs, resolveMetricValue, selectedVehicle, vehicleMetricsSeries]);

  useEffect(() => {
    if (!availableMetricsTabs.length) {
      return;
    }
    const isActiveAvailable = availableMetricsTabs.some(
      (tab) => tab.id === activeMetricsTab
    );
    if (!isActiveAvailable) {
      setActiveMetricsTab(availableMetricsTabs[0].id);
    }
  }, [availableMetricsTabs, activeMetricsTab]);

  const activeTabConfig = useMemo(() => {
    if (!availableMetricsTabs.length) return null;
    return (
      availableMetricsTabs.find((tab) => tab.id === activeMetricsTab) ||
      availableMetricsTabs[0] ||
      null
    );
  }, [availableMetricsTabs, activeMetricsTab]);

  const activeMetricDisplayValue = useMemo(() => {
    if (!selectedVehicle || !activeTabConfig) {
      return intl.formatMessage({
        id: "monitor.bottomBar.noData",
        defaultMessage: "No data available",
      });
    }
    const metricValue = resolveMetricValue(selectedVehicle, activeTabConfig.keys);
    return formatMetricValue(metricValue, activeTabConfig.unit);
  }, [
    activeTabConfig,
    formatMetricValue,
    intl,
    resolveMetricValue,
    selectedVehicle,
  ]);

  useEffect(() => {
    if (!selectedVehicleHistoryKey) return;
    const snapshot = selectedVehicleRef.current;
    if (!snapshot) return;
    fetchHistoricalPositions(snapshot);
  }, [fetchHistoricalPositions, selectedVehicleHistoryKey]);

  // Track page visibility to control animations
  useEffect(() => {
    const handleVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const maplayerKeyMap = {
    hybridMap: intl.formatMessage({ id: "routingmap.hybridMap" }),
    satelliteMap: intl.formatMessage({ id: "routingmap.satelliteMap" }),
    googleTerrain: intl.formatMessage({ id: "routingmap.googleTerrain" }),
    trafficMap: intl.formatMessage({ id: "routingmap.trafficMap" }),
    hybirdtrafficMap: intl.formatMessage({ id: "routingmap.hybirdtrafficMap" }),
  };

  useEffect(() => {
    if (!mapInstance) {
      initMap();
    }
    // cleanup on unmount
    return () => {
      if (mapInstance) {
        mapInstance.remove();
        mapInstance = null;
        equipmentMarkersRef.current = {};
      }
    };
  }, []);

  useEffect(() => {
    if (!window.google && !document.querySelector('#google-maps-script')) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_KEY}&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => { };
      document.body.appendChild(script);
    } else {
      // Google Maps API already loaded
    }
  }, []);

  useEffect(() => {
    if (authUser && layerControlRef.current == null) {
      var layers = tileLayers(intl);

      if (authUser?.company?.trafficLayer) {
        layers[maplayerKeyMap.trafficMap] = L.gridLayer
          .googleMutant({ type: "roadmap", model: "traffic" })
          .addGoogleLayer("TrafficLayer");
        layers[maplayerKeyMap.hybirdtrafficMap] = L.gridLayer
          .googleMutant({ type: "hybrid", model: "traffic" })
          .addGoogleLayer("TrafficLayer");
      }

      const selectedLayerKey = authUser?.company?.maplayer || "hybridMap";
      const selectedLayer = layers[maplayerKeyMap[selectedLayerKey]];
      if (selectedLayer) {
        selectedLayer.addTo(mapInstance);
      }

      // Remove previous control if it exists
      if (layerControlRef.current) {
        //   mapInstance.removeControl(layerControlRef.current);
        layerControlRef.current = null;
      }

      // Add new control and save reference
      layerControlRef.current = L.control.layers(layers, null, {
        position: 'bottomright',
      }).addTo(mapInstance);


      markerLayerRef.current = L.layerGroup().addTo(mapInstance);
      getGeoZone();
    }
  }, [authUser]);

  useEffect(() => {
    if (geoZones) {
      drawGeoZones();
    }
  }, [geoZones]);



  const getGeoZone = async () => {
    try {
      let apiPath = "geozones?page=0&limit=1000&filter_companyId=" + authUser.company.id;
      let response = await api.getApi(apiPath);
      if (response.status == "success") {
        setGeoZones(response.data.rows);
      }
    } catch (error) {
      console.error('Error fetching geozones:', error);
    }

  }

  const initMap = () => {


    //const selectedLayer = 'hybridMap';

    const defaultCenter = process.env.REACT_APP_MAP_CENTER
      ? process.env.REACT_APP_MAP_CENTER.split(',').map(Number)
      : [-33.447487, -70.673676];
    mapInstance = L.map("map", {
      center: defaultCenter,
      zoom: Number(process.env.REACT_APP_MAP_ZOOM) || 10,
      maxZoom: 20,
      scrollWheelZoom: true,
      //layers: [layers[intl.formatMessage({ id: `routingmap.${selectedLayer}` })]],
    });



    mapInstance.zoomControl.setPosition("bottomright");
    mapRef.current = mapInstance;

    const handleMoveStart = () => {
      if (programmaticMoveRef.current) {
        programmaticMoveRef.current = false;
        return;
      }
      lastUserInteractionRef.current = Date.now();
      if (isFollowingVehicleRef.current) {
        isFollowingVehicleRef.current = false;
        setIsFollowingVehicle(false);
      }
    };

    mapInstance.on("movestart", handleMoveStart);
    mapInstance.on("dragstart", handleMoveStart);
    mapInstance.on("zoomstart", handleMoveStart);

  };

  // AuthUser via postMessage
  useEffect(() => {
    const handleMessage = ({ origin, data }) => {
      const ALLOWED_DOMAIN = ".intellitech.cl";
      console.log("Received message from origin:", origin, 'data===', data);
      const isAllowedOrigin =
        origin === process.env.REACT_APP_LOCALHOST_URL ||
        origin === process.env.REACT_APP_FRONT_BASE_URL ||
        origin.endsWith(ALLOWED_DOMAIN); // <--- Key Change for Subdomain check
      if (
        isAllowedOrigin &&
        data?.type === "SHARE_DATA"
      ) {
        setAuthUser(data.payload.authUser);
        localStorage.setItem("authUser", JSON.stringify(data.payload.authUser));
      }
    };
    window.addEventListener("message", handleMessage);
    const stored = localStorage.getItem("authUser");
    if (stored) setAuthUser(JSON.parse(stored));
    return () => window.removeEventListener("message", handleMessage);
  }, []);


  const query = useMemo(() => {
    if (!authUser) return null;
    let groupId = getGroupFilterQuery("equipmentId", undefined, authUser);
    let parentGroupId = null;
    if (group && group.value != -1) {
      groupId = group.value;
    } else if (parentGroup && parentGroup.value != -1) {
      parentGroupId = parentGroup.value;
    }
    return {
      companyId: getCompanyId(authUser),
      imei: null,
      format: getCompanyFormat(authUser),
      token: authUser.token,
      equipmentId: null,
      event: "monitorgps",
      groupId: groupId ? groupId : getGroupFilterQuery("equipmentId", undefined, authUser),
      parentGroupId: parentGroupId,
    };
  }, [authUser, group, parentGroup]);


  useEffect(() => {
    let isMounted = true;

    if (!authUser) {
      setHasParentGroupOptions(null);
      setHasGroupOptions(null);
      return () => {
        isMounted = false;
      };
    }

    const evaluateFilterAvailability = async () => {
      try {
        const parentGroupPromise = authUser?.parentGroup?.length
          ? getParentGroupDetails(
            "",
            [],
            null,
            authUser?.company ?? null,
            false,
            authUser
          )
          : Promise.resolve(null);

        const groupPromise = getGroupSearchDetails(
          "",
          [],
          null,
          authUser?.company ?? null,
          false,
          authUser
        );

        const [parentGroupResponse, groupResponse] = await Promise.all([
          parentGroupPromise,
          groupPromise,
        ]);

        if (!isMounted) return;

        if (parentGroupResponse) {
          setHasParentGroupOptions(
            Array.isArray(parentGroupResponse.options) &&
            parentGroupResponse.options.length > 0
          );
        } else {
          setHasParentGroupOptions(null);
        }

        setHasGroupOptions(
          Array.isArray(groupResponse?.options) &&
          groupResponse.options.length > 0
        );
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to load monitor filter availability", error);
        if (authUser?.parentGroup?.length) {
          setHasParentGroupOptions(false);
        } else {
          setHasParentGroupOptions(null);
        }
        setHasGroupOptions(false);
      }
    };

    evaluateFilterAvailability();

    return () => {
      isMounted = false;
    };
  }, [authUser]);

  useEffect(() => {
    if (hasParentGroupOptions === false && parentGroup) {
      setParentGroup(null);
    }
  }, [hasParentGroupOptions, parentGroup]);

  useEffect(() => {
    if (hasGroupOptions === false && group) {
      setGroup(null);
    }
  }, [hasGroupOptions, group]);



  // Smooth animation helper
  const animateMarker = useCallback((marker, toLatLng, speed = 0) => {
    if (!marker) return;

    const [toLat, toLng] = Array.isArray(toLatLng) ? toLatLng : [];

    // ❌ invalid coords = skip (prevents ocean jump)
    if (
      !Number.isFinite(toLat) ||
      !Number.isFinite(toLng) ||
      Math.abs(toLat) > 90 ||
      Math.abs(toLng) > 180
    ) return;

    // 🛑 stop previous animation
    if (marker._raf) {
      cancelAnimationFrame(marker._raf);
      marker._raf = null;
    }

    const from = marker.getLatLng();
    if (!from) {
      marker.setLatLng([toLat, toLng]);
      return;
    }

    const distance = from.distanceTo([toLat, toLng]);

    // 🧯 ignore GPS jitter
    if (distance < 2) return;

    // 👁 tab not visible → instant move
    if (!pageVisible) {
      marker.setLatLng([toLat, toLng]);
      return;
    }

    // ⏱ clamp duration (prevents flicker)
    const duration =
      speed > 1
        ? Math.min(Math.max(distance / (speed / 3.6), 300), 1500)
        : 1200;

    const start = performance.now();

    function step(now) {
      const t = Math.min((now - start) / duration, 1);

      const lat = from.lat + (toLat - from.lat) * t;
      const lng = from.lng + (toLng - from.lng) * t;

      marker.setLatLng([lat, lng]);

      if (t < 1) {
        marker._raf = requestAnimationFrame(step);
      }
    }

    marker._raf = requestAnimationFrame(step);
  }, [pageVisible]);

  const detachPopupHandlers = useCallback((marker) => {
    const handlers = marker?._popupHandlers;
    if (!handlers || !handlers.length) {
      return;
    }
    handlers.forEach(({ element, handler }) => {
      if (element && handler) {
        element.removeEventListener("click", handler);
      }
    });
    marker._popupHandlers = null;
  }, []);

  const attachPopupHandlers = useCallback(
    (marker) => {
      if (!marker || typeof document === "undefined") return;
      detachPopupHandlers(marker);
      const registerNumber = marker._registerNumber;
      if (!registerNumber) return;

      const handlers = [];
      const bind = (id, handler) => {
        const element = document.getElementById(id);
        if (!element) return;
        element.addEventListener("click", handler);
        handlers.push({ element, handler });
      };

      bind(`popupButton-${registerNumber}`, () => {
        const { lat, lng } = marker.getLatLng();
        setStreetLocation({
          latitude: lat,
          longitude: lng,
        });
        setStreetViewModal(true);
      });

      bind(`googleButton-${registerNumber}`, (event) => {
        event.preventDefault();
        event.stopPropagation();
        const { lat, lng } = marker.getLatLng();
        const url = `https://www.google.com/maps?q=${lat},${lng}`;
        window.open(url, "_blank", "noopener");
      });

      bind(`wazeButton-${registerNumber}`, (event) => {
        event.preventDefault();
        event.stopPropagation();
        const { lat, lng } = marker.getLatLng();
        const url = `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`;
        window.open(url, "_blank", "noopener");
      });

      bind(`streetViewButton-${registerNumber}`, (event) => {
        event.preventDefault();
        event.stopPropagation();
        const { lat, lng } = marker.getLatLng();
        const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
        window.open(url, "_blank", "noopener");
      });

      marker._popupHandlers = handlers;
    },
    [detachPopupHandlers, setStreetLocation, setStreetViewModal]
  );

  useEffect(() => {
    if (!authUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    if (equipmentMarkersRef.current) {
      Object.entries(equipmentMarkersRef.current).forEach(
        ([registerNumber, marker]) => {
          detachPopupHandlers(marker);
          markerLayerRef.current?.removeLayer(marker);
          delete equipmentMarkersRef.current[registerNumber];
        }
      );
      activePopupRef.current = null;
      firstFitRef.current = true;
    }
    equipmentsByIdRef.current.clear();
    equipmentsOrderRef.current = [];
    equipmentsRef.current = [];
    latestPositionsRef.current = {};
    polyLineLayerRef.current = [];
    focusedMarkersRef.current.clear();
    scheduleEquipmentsState([], { immediate: true });
  }, [authUser, detachPopupHandlers, group, parentGroup, scheduleEquipmentsState]);




  // Create or update markers
  const updateOrCreateMarkers = useCallback(
    (data) => {
      const layer = markerLayerRef.current;
      if (!layer) return;
      if (Array.isArray(data)) {
        let selectedVehicleStillVisible = false;
        const formatKey = getCompanyFormat(authUser);
        data
          .filter(
            (key) =>
              Number.isFinite(parseFloat(key.latitude)) &&
              Number.isFinite(parseFloat(key.longitude))
          )
          .forEach((eq) => {
            const {
              registerNumber,
              latitude = 0,
              longitude = 0,
              course = 0,
            } = eq;
            const latNum = parseFloat(latitude);
            const lngNum = parseFloat(longitude);
            if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
              return;
            }
            eq.speed = convertToFormat(eq.speed, 'kmh');
            let marker = equipmentMarkersRef.current[registerNumber];
            latestPositionsRef.current[registerNumber] = { lat: latNum, lng: lngNum };
            const iconKey = buildMarkerIconKey(eq, formatKey);

            const attachClick = (m) => {
              m.off('click');
              m.on('click', () => {
                // Clear any selected vehicle in the filter when user clicks a marker
                setEquipment(null);
                setSearchTerm("");
                focusedMarkersRef.current.clear();
                handleVehicleSelection(eq);

                focusMapView(m, 17, { force: true });
                m.openPopup();
              });
            };

            if (marker) {
              // smooth-move
              animateMarker(marker, [latNum, lngNum], parseFloat(eq.speed));
              if (marker._iconKey !== iconKey) {
                const { html, width, height } = getHtmlMarker(authUser, course, eq);
                const icon = L.divIcon({
                  html,
                  className: "",
                  iconSize: [width, height],
                  iconAnchor: [width / 2, height],
                  popupAnchor: [0, -height]
                });
                marker.setIcon(icon);
                marker._iconKey = iconKey;
              }
              marker._latestEntry = eq;
              marker._registerNumber = registerNumber;
              if (activePopupRef.current === marker) {
                marker.setPopupContent(showContent(eq, authUser, intl)); // Ensures latest popup content
                const popupInstance = marker.getPopup();
                if (popupInstance) popupInstance.options.autoPan = false;
                attachPopupHandlers(marker);
              }
              attachClick(marker);
            } else {
              const { html, width, height } = getHtmlMarker(authUser, course, eq);
              const icon = L.divIcon({
                html,
                className: "",
                iconSize: [width, height],
                iconAnchor: [width / 2, height],
                popupAnchor: [0, -height]
              });
              // create new
              marker = L.marker([latNum, lngNum], { icon })
              marker.bindPopup(showContent(eq, authUser, intl), { autoPan: false });
              marker._iconKey = iconKey;
              marker._latestEntry = eq;
              marker._registerNumber = registerNumber;
              attachClick(marker);

              marker.on("popupopen", () => {
                if (activePopupRef.current && activePopupRef.current !== marker) {
                  activePopupRef.current.closePopup();
                }
                activePopupRef.current = marker;
                focusedMarkersRef.current.clear();
                const latestEntry = marker._latestEntry || eq;
                marker.setPopupContent(showContent(latestEntry, authUser, intl));
                const popupInstance = marker.getPopup();
                if (popupInstance) popupInstance.options.autoPan = false;
                attachPopupHandlers(marker);

                const latLng = marker.getLatLng();
                const map = mapRef.current;
                if (map) {
                  const point = map.latLngToContainerPoint(latLng);
                  point.y -= 80;
                  const newLatLng = map.containerPointToLatLng(point);
                  map.panTo(newLatLng, { animate: true });
                }
              });

              // Optional: clean up
              marker.on("popupclose", () => {
                if (activePopupRef.current === marker) {
                  activePopupRef.current = null;
                }
                detachPopupHandlers(marker);
              });
              equipmentMarkersRef.current[registerNumber] = marker;
              layer.addLayer(marker);
            }

            const currentSelected = selectedVehicleRef.current;
            if (
              currentSelected &&
              (currentSelected.id === eq.id ||
                (currentSelected.registerNumber &&
                  currentSelected.registerNumber === registerNumber))
            ) {
              selectedVehicleStillVisible = true;
              const keysToCompare = [
                "fixtime",
                "speed",
                "latitude",
                "longitude",
                "batteryVoltage",
                "batteryAmperage",
                "battery_voltage",
                "battery_amperage",
              ];
              const telemetryChanged =
                JSON.stringify(currentSelected?.telemetry ?? null) !==
                JSON.stringify(eq?.telemetry ?? null);
              const hasChanged =
                telemetryChanged ||
                keysToCompare.some((key) => currentSelected[key] !== eq[key]);
              if (hasChanged) {
                const updatedSnapshot = { ...currentSelected, ...eq };
                if (currentSelected?.telemetry || eq?.telemetry) {
                  updatedSnapshot.telemetry = {
                    ...(currentSelected?.telemetry || {}),
                    ...(eq?.telemetry || {}),
                  };
                }
                handleVehicleSelection(updatedSnapshot, {
                  resetTab: false,
                  forceExpand: false,
                  skipHistory: true,
                });
              }
              if (isFollowingVehicleRef.current) {
                focusMapView(marker || [latNum, lngNum], 17, { force: true });
              }
            }
          });

        const currentSelected = selectedVehicleRef.current;
        if (currentSelected && !selectedVehicleStillVisible) {
          handleVehicleSelection(null);
        }
      }

    },
    [
      authUser,
      animateMarker,
      attachPopupHandlers,
      detachPopupHandlers,
      focusMapView,
      handleVehicleSelection,
      intl,
    ]
  );

  const scheduleMarkerUpdates = useCallback(
    (updates) => {
      if (!updates) return;
      const pending = pendingMarkerUpdatesRef.current;
      const entries = Array.isArray(updates) ? updates : [updates];
      entries.forEach((entry) => {
        if (!entry) return;
        const key =
          entry.id ??
          entry.registerNumber ??
          entry.imei ??
          `${entry.deviceId ?? ""}-${entry.timestamp ?? ""}`;
        if (key === "" || key === "-" || key === undefined) return;
        pending.set(key, entry);
      });
      if (markerUpdateRafRef.current != null) {
        return;
      }
      markerUpdateRafRef.current = requestAnimationFrame(() => {
        markerUpdateRafRef.current = null;
        const merged = Array.from(pending.values());
        pending.clear();
        if (merged.length) {
          updateOrCreateMarkers(merged);
        }
      });
    },
    [updateOrCreateMarkers]
  );

  useEffect(() => {
    return () => {
      if (equipmentsStateTimerRef.current) {
        clearTimeout(equipmentsStateTimerRef.current);
        equipmentsStateTimerRef.current = null;
      }
      if (markerUpdateRafRef.current != null) {
        cancelAnimationFrame(markerUpdateRafRef.current);
        markerUpdateRafRef.current = null;
      }
    };
  }, []);

  // When page becomes visible again, jump markers to their last known positions
  useEffect(() => {
    if (!pageVisible) return;
    Object.entries(equipmentMarkersRef.current).forEach(([id, marker]) => {
      const pos = latestPositionsRef.current[id];
      if (pos) {
        marker.setLatLng([pos.lat, pos.lng]);
      }
    });
  }, [pageVisible]);

  const drawGeoZones = () => {
    if (!geoZones?.length || polyLineLayerRef.current.length > 0) return;

    geoZones.forEach((zone) => {
      const shape = parse(zone.area);
      const coords = shape?.coordinates;
      const options = { color: zone.activeColor || 'blue', weight: 4 };
      const poly = shape.type === "LineString" ? new L.polyline(coords, options) : new L.polygon(coords, options);

      poly.bindPopup(`<strong>${zone.name}</strong>`).addTo(mapInstance);
      poly.on('popupopen', (e) => {
        // const mid = Math.floor(e.popup._source._latlngs.length / 2);
        // e.popup.setLatLng(e.popup._source._latlngs[mid]);
      });
    });

    polyLineLayerRef.current.push(...geoZones);
  };


  const streetViewCancel = () => {
    setStreetViewModal(false);
  };


  // One-time fit to all markers
  useEffect(() => {
    if (!equipments.length || !mapRef.current || !firstFitRef.current) return;
    const bounds = L.latLngBounds();
    equipments.forEach((eq) => {
      if (eq.latitude && eq.longitude) {
        bounds.extend([eq.latitude, eq.longitude]);
      }
    });
    if (bounds.isValid()) {
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
    firstFitRef.current = false;
  }, [equipments, searchTerm]);

  // WebSocket hookup
  useEffect(() => {
    if (!query) return;
    const socket = socketIOClient(webSocket, {
      path: "/websocket",
      transports: ["websocket"],
      query: { data: JSON.stringify(query) },
    });
    socketRef.current = socket;

    socket.on("totalEquipments", (data) => {
      setLoading(false);
      const list = Array.isArray(data) ? data : [];
      const formatKey = getCompanyFormat(authUser);
      let updatedArray = list.map((item) => {
        const labelText = resolveEquipmentLabel(item, formatKey);
        return {
          ...item,
          label: labelText,
          value: item.id,
          searchTokens: buildEquipmentSearchTokens(labelText),
        };
      });
      if (updatedArray.length > 0) {
        updatedArray = [
          { value: -1, label: "Todos", searchTokens: [] },
          ...updatedArray,
        ];
      }
      setEquipmentsOptions(updatedArray);
      equipmentsByIdRef.current = new Map(list.map((item) => [item.id, item]));
      equipmentsOrderRef.current = list.map((item) => item.id);
      equipmentsRef.current = list;
      scheduleEquipmentsState(list, { immediate: true });
      scheduleMarkerUpdates(list);
    });

    socket.on("monitor_gps_error", (error) => {
      setLoading(false);
      equipmentsByIdRef.current.clear();
      equipmentsOrderRef.current = [];
      equipmentsRef.current = [];
      scheduleEquipmentsState([], { immediate: true });
      // alert(error.error);
    });

    socket.on("monitor_gps_response", (updatedData) => {
      if (updatedData) {
        const updates = Array.isArray(updatedData) ? updatedData : [updatedData];
        const byId = equipmentsByIdRef.current;
        updates.forEach((entry) => {
          if (!entry || entry.id == null) return;
          if (!byId.has(entry.id)) {
            equipmentsOrderRef.current.push(entry.id);
          }
          byId.set(entry.id, entry);
        });
        const nextList = buildEquipmentsList();
        equipmentsRef.current = nextList;
        scheduleEquipmentsState(nextList);
      }
      scheduleMarkerUpdates(updatedData);
    });
    socket.on("intellitech:positions", handleRedisStream);
    const iid = setInterval(
      () => socket.emit("monitor_gps_request", { data: JSON.stringify(query) }),
      2000
    );
    return () => {
      clearInterval(iid);
      socket.off("intellitech:positions", handleRedisStream);
      socket.disconnect();
    };
  }, [
    authUser,
    buildEquipmentsList,
    handleRedisStream,
    query,
    scheduleEquipmentsState,
    scheduleMarkerUpdates,
  ]);

  // Debounced search
  const debouncedFilter = useMemo(
    () =>
      debounce((term) => {
        if (!mapRef.current || !term) return;
        const match = equipmentsRef.current.find(
          (eq) =>
            eq.registerNumber?.toLowerCase().includes(term.toLowerCase()) ||
            eq.name?.toLowerCase().includes(term.toLowerCase())
        );
        if (match) {
          if (!focusedMarkersRef.current.has(match.id)) {
            focusedMarkersRef.current.add(match.id);
            const m = equipmentMarkersRef.current[match.registerNumber];
            if (m) {
              focusMapView(m, 17, { force: true });
            }
          }
          // If already focused, do not keep re-centering on updates.
        }
      }, 300),
    [focusMapView]
  );

  useEffect(() => {
    debouncedFilter(searchTerm);
  }, [searchTerm, debouncedFilter]);

  useEffect(() => {
    if (!equipment) {
      previousEquipmentValueRef.current = null;
      return;
    }

    const equipmentKey =
      equipment?.value != null ? equipment.value : equipment?.label ?? null;
    const equipmentChanged =
      previousEquipmentValueRef.current !== equipmentKey;

    if (equipmentChanged) {
      previousEquipmentValueRef.current = equipmentKey;
      focusedMarkersRef.current.clear();
      if (activePopupRef.current) {
        activePopupRef.current.closePopup();
        activePopupRef.current = null;
      }
    }

    if (equipment.value === -1) {
      if (equipmentChanged) {
        firstFitRef.current = true;
        setSearchTerm("");
        if (isFollowingVehicleRef.current) {
          isFollowingVehicleRef.current = false;
          setIsFollowingVehicle(false);
        }
      }
      return;
    }

    if (equipmentChanged) {
      setSearchTerm(
        typeof equipment.label === "string" ? equipment.label : ""
      );
    }

    const match =
      equipments.find((item) => item.id === equipment.value) ??
      equipments.find((item) => item.registerNumber === equipment.label);

    if (!match) {
      return;
    }

    const currentSelection = selectedVehicleRef.current;
    if (
      currentSelection &&
      ((currentSelection.id != null && currentSelection.id === match.id) ||
        (currentSelection.registerNumber &&
          currentSelection.registerNumber === match.registerNumber))
    ) {
      return;
    }

    handleVehicleSelection(match, {
      resetTab: true,
      forceExpand: true,
      skipHistory: false,
    });

    const marker = equipmentMarkersRef.current[match.registerNumber];
    if (marker) {
      focusMapView(marker, 17, { force: true });
      marker.openPopup();
    } else {
      const point = getEntryCoordinates(match);
      if (point) {
        focusMapView(point, 16, { force: true });
      }
    }
  }, [
    equipment,
    equipments,
    focusMapView,
    getEntryCoordinates,
    handleVehicleSelection,
  ]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const toggleBottomBar = useCallback(() => {
    setIsBottomBarCollapsed((prev) => !prev);
  }, []);

  const closeBottomBar = useCallback(() => {
    setEquipment(null);
    setSearchTerm("");
    focusedMarkersRef.current.clear();
    handleVehicleSelection(null);
  }, [handleVehicleSelection]);

  useEffect(() => {
    return () => {
      clearShortHistory();
    };
  }, [clearShortHistory]);

  const renderActions = (inline = false) => {
    const canCenter = Boolean(selectedVehicleRef.current);
    const centerTooltip = intl.formatMessage({
      id: "monitor.bottomBar.actions.center",
      defaultMessage: "Center on map",
    });
    const followLabel = isFollowingVehicle
      ? intl.formatMessage({
        id: "monitor.bottomBar.actions.follow.stop",
        defaultMessage: "Stop following",
      })
      : intl.formatMessage({
        id: "monitor.bottomBar.actions.follow.start",
        defaultMessage: "Follow vehicle",
      });

    return (
      <div
        className={`vehicle-bottom-bar__actions${inline ? " vehicle-bottom-bar__actions--inline" : ""
          }`}
      >
        <button
          type="button"
          className="vehicle-bottom-bar__action vehicle-bottom-bar__action--history"
          onClick={handleShortHistoryClick}
          disabled={metricsLoading || !canCenter}
        >
          {shortHistoryActive
            ? intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.hide",
              defaultMessage: "Hide Short History",
            })
            : intl.formatMessage({
              id: "monitor.bottomBar.shortHistory.show",
              defaultMessage: "Short History",
            })}
        </button>
        <button
          type="button"
          className="vehicle-bottom-bar__action vehicle-bottom-bar__action--icon"
          onClick={handleCenterOnVehicle}
          title={centerTooltip}
          aria-label={centerTooltip}
          disabled={!canCenter}
        >
          <svg
            className="vehicle-bottom-bar__icon"
            viewBox="0 0 24 24"
            role="img"
            aria-hidden="true"
          >
            <path d="M11 5V2h2v3a5.002 5.002 0 0 1 4 4h3v2h-3a5.002 5.002 0 0 1-4 4v3h-2v-3a5.002 5.002 0 0 1-4-4H2V9h3a5.002 5.002 0 0 1 4-4Zm1 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 2a5 5 0 0 1-4-2H4v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4h-4a5 5 0 0 1-4 2Z" />
          </svg>
        </button>
        <button
          type="button"
          className={`vehicle-bottom-bar__action vehicle-bottom-bar__action--follow${isFollowingVehicle ? " is-active" : ""
            }`}
          onClick={toggleFollowVehicle}
          title={followLabel}
          aria-label={followLabel}
          aria-pressed={isFollowingVehicle}
          disabled={!canCenter && !isFollowingVehicle}
        >
          {followLabel}
        </button>
        <button
          type="button"
          className="vehicle-bottom-bar__action vehicle-bottom-bar__action--toggle"
          onClick={toggleBottomBar}
          aria-expanded={!isBottomBarCollapsed}
        >
          {isBottomBarCollapsed
            ? intl.formatMessage({
              id: "monitor.bottomBar.expand",
              defaultMessage: "Show",
            })
            : intl.formatMessage({
              id: "monitor.bottomBar.collapse",
              defaultMessage: "Hide",
            })}
        </button>
        <button
          type="button"
          className="vehicle-bottom-bar__action vehicle-bottom-bar__action--close"
          onClick={closeBottomBar}
        >
          {intl.formatMessage({ id: "close", defaultMessage: "Close" })}
        </button>
      </div>
    );
  };

  const shouldShowParentGroupFilter = Boolean(authUser?.parentGroup?.length) && hasParentGroupOptions !== false;
  const shouldShowGroupFilter = Boolean(authUser) && hasGroupOptions !== false;

  return (
    <div className="viewdiv">
      {/* Search box */}
      <div className="filters">
        {/* <div style={{width:"18%"}}>
          <input
            type="text"
            placeholder="Buscar por Nombre o Patente"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: "95%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        </div> */}

        {shouldShowParentGroupFilter && <div className="dropDowns">
          <label>{intl.formatMessage({ id: "gps.select.parentgroup" })}</label>
          <AsyncPaginate
            value={parentGroup}
            onChange={value => {
              setParentGroup(value)
              setGroup(null);
              setEquipment(null);
              setSearchBy(Math.random());
              setSearchBy1(Math.random());
            }}
            loadingMessage={() => intl.formatMessage({ id: "loading" })}
            noOptionsMessage={() => intl.formatMessage({ id: "nooptions" })}
            loadOptions={(search, loadedOptions) =>
              getParentGroupDetails(
                search,
                loadedOptions,
                null,
                authUser?.company ?? null,
                true,
                authUser
              )
            }
            placeholder={intl.formatMessage({ id: "gps.select.parentgroup" })}
          />
        </div>}
        {shouldShowGroupFilter && <div className="dropDowns">
          <label>{intl.formatMessage({ id: "gps.select.group" })}</label>
          <AsyncPaginate
            value={group}
            onChange={value => {
              setGroup(value)
              setEquipment(null);
              setSearchBy1(Math.random());
            }}
            key={searchBy}
            loadingMessage={() => intl.formatMessage({ id: "loading" })}
            noOptionsMessage={() => intl.formatMessage({ id: "nooptions" })}
            loadOptions={(search, loadedOptions) =>
              getGroupSearchDetails(
                search,
                loadedOptions,
                { parentGroupId: parentGroup },
                authUser?.company ?? null,
                true,
                authUser
              )
            }
            placeholder={intl.formatMessage({ id: "gps.select.group" })}
          />
        </div>}
        {(authUser) && <div class="dropDowns">
          <label>{intl.formatMessage({ id: "gps.select.nameregisternumber" })}</label>
          <Select
            options={equipmentsOptions}
            value={equipment}
            onChange={value => setEquipment(value)}
            isSearchable
            filterOption={equipmentFilterOption}
            noOptionsMessage={() => intl.formatMessage({ id: "nooptions" })}
            placeholder={intl.formatMessage({ id: "gps.select.nameregisternumber" })}
          />
          {/* <AsyncPaginate
            value={equipment}
            onChange={value => setEquipment(value)}
            key={searchBy1}
            loadingMessage={() => intl.formatMessage({ id: "loading" })}
            noOptionsMessage={() => intl.formatMessage({ id: "nooptions" })}
            loadOptions={(search, prevOptions) =>
              getEquipmentFilterDetails(
                search,
                prevOptions,
                { group: group, parentGroupId: parentGroup, internal: true },
                authUser?.company ?? null,
                true,
                authUser && authUser.company.format ? authUser.company.format : "registerNumber",
                authUser
              )
            }
            placeholder={intl.formatMessage({ id: "gps.select.nameregisternumber" })}
          /> */}
        </div>}


      </div>
      <div className="monitor-stats">
        <div className="monitor-stats__grid">
          {monitorStatsCards.map((card) => (
            <div
              key={card.key}
              className={`monitor-stats__card monitor-stats__card--${card.accent}`}
              title={card.label}
              aria-label={`${card.label}: ${card.value}`}
            >
              <span className="monitor-stats__icon" aria-hidden="true">
                {card.icon}
              </span>
              <span className="monitor-stats__value">{card.value}</span>
              <span className="monitor-stats__sr">{card.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div id="map" data-testid="map" />
      {/* Loading overlay */}
      {loading && (
        <div className="spinnerdiv" >
          <img
            src={loadingspinner}
            alt="loading"
            style={{ width: 30, height: 30 }}
          />
        </div>
      )}

      {selectedVehicle && (
        <div
          className={`vehicle-bottom-bar ${isBottomBarCollapsed ? "collapsed" : "expanded"}`}
        >
          <div className="vehicle-bottom-bar__header">
            <div className="vehicle-bottom-bar__summary">
              {(isBottomBarCollapsed || !availableMetricsTabs.length) && (
                <span className="vehicle-bottom-bar__title">
                  {selectedVehicleLabel ||
                    intl.formatMessage({
                      id: "monitor.bottomBar.vehiclePlaceholder",
                      defaultMessage: "Selected vehicle",
                    })}
                </span>
              )}
            </div>
            {isBottomBarCollapsed && renderActions()}
          </div>
          {!isBottomBarCollapsed && (
            <>
              <div className="vehicle-bottom-bar__controls">
                <span className="vehicle-bottom-bar__vehicle-tag">
                  {selectedVehicleLabel ||
                    intl.formatMessage({
                      id: "monitor.bottomBar.vehiclePlaceholder",
                      defaultMessage: "Selected vehicle",
                    })}
                </span>
                <div className="vehicle-bottom-bar__tabs">
                  {availableMetricsTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`vehicle-bottom-bar__tab ${tab.id === activeMetricsTab ? "is-active" : ""
                        }`}
                      onClick={() => setActiveMetricsTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {renderActions(true)}
              </div>
              <div className="vehicle-bottom-bar__content">
                <div className="vehicle-bottom-bar__metric">
                  <span className="vehicle-bottom-bar__metric-label">
                    {intl.formatMessage({
                      id: "monitor.bottomBar.latestValue",
                      defaultMessage: "Latest value",
                    })}
                  </span>
                  <span className="vehicle-bottom-bar__metric-title">
                    {activeTabConfig?.label}
                  </span>
                  <span className="vehicle-bottom-bar__metric-value">
                    {(activeMetricDisplayValue)}
                  </span>
                  {selectedVehicle?.fixtime && (
                    <span className="vehicle-bottom-bar__meta">
                      {intl.formatMessage({
                        id: "monitor.bottomBar.updated",
                        defaultMessage: "Last update",
                      })}
                      : {formatDateTimeLabel(selectedVehicle.fixtime)}
                    </span>
                  )}
                </div>
                <div className="vehicle-bottom-bar__chart">
                  <MetricLineChart
                    intl={intl}
                    unit={activeTabConfig?.unit}
                    data={
                      activeTabConfig
                        ? vehicleMetricsSeries[activeTabConfig.id] || []
                        : []
                    }
                    loading={metricsLoading}
                    error={metricsError}
                    highlightTimestamp={shortHistoryHighlight}
                    config={activeTabConfig?.chart}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {IsStreetViewModal && (
        <StreetViewModalCard open={IsStreetViewModal} location={streetLocation} onCancel={streetViewCancel} />
      )}
    </div>
  );
}
